import classNames from 'classnames';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Section } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { useRanking } from '@/routes/system/_auth/surveys/-hooks';
import { wrapTextInPrint } from '@/styles/print.css';
import { formatNumber, type ScoreKey } from '@/utils';
import * as styles from './index.css';

type Item = {
    readonly name: string;
    readonly di: number | null;
    readonly deviation: number | null;
};

interface RankingProps {
    title: string;
    variant: 'positive' | 'negative';
    /**
     * A list of items to rank, or `null` while still loading
     */
    items: Item[] | null;
    /**
     * The number of items to display
     * @default 3
     */
    limit?: number;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
}

export function Ranking({ items, isError, onRetry, title, ...props }: RankingProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message={`${title}の取得に失敗しました。`} />;

    return items === null ? <RankingSkeleton {...props} /> : <RankingContent {...props} title={title} items={items} />;
}

type RankingContentProps = Omit<RankingProps, 'items'> & {
    items: NonNullable<RankingProps['items']>;
};

function RankingContent({ title, variant, items, limit = 3 }: RankingContentProps) {
    const [scoreKey, setScoreKey] = useState<ScoreKey>('di');
    const { best, worst } = useRanking(scoreKey, items, { limit });
    const ranking = variant === 'positive' ? best : worst;

    return (
        <Flex as={Section} pt="8" pb="8" direction="column" gap="4" flex>
            <div className={styles.iconCircleVariants[variant]}>
                <Icon
                    icon={variant === 'positive' ? ThumbsUp : ThumbsDown}
                    size="lg"
                    className={styles.iconVariants[variant]}
                />
            </div>

            {/* TODO: Implement help tooltip */}
            <div className={styles.title}>{title}</div>

            <div>
                <Select value={scoreKey} onChange={(e) => setScoreKey(e.target.value as ScoreKey)}>
                    <option value="di">DI</option>
                    <option value="deviation">偏差値</option>
                </Select>
            </div>

            <Flex as="ol" direction="column" gap="6">
                {ranking.map((item, idx) => (
                    <Flex as="li" key={item.name} justify="space-between" align="center" gap="4">
                        <span title={item.name} className={classNames(styles.nameVariants[variant], wrapTextInPrint)}>
                            {idx + 1}. {item.name}
                        </span>

                        <span>
                            {scoreKey === 'di' && (
                                <span>
                                    DI{' '}
                                    {item.di !== null ? (
                                        <span className={styles.scoreVariants[variant]}>{formatNumber(item.di)}</span>
                                    ) : (
                                        'N/A'
                                    )}
                                </span>
                            )}
                            {scoreKey === 'deviation' && (
                                <span>
                                    偏差値{' '}
                                    {item.deviation !== null ? (
                                        <span className={styles.scoreVariants[variant]}>
                                            {formatNumber(item.deviation)}
                                        </span>
                                    ) : (
                                        'N/A'
                                    )}
                                </span>
                            )}
                        </span>
                    </Flex>
                ))}
            </Flex>
        </Flex>
    );
}

export function RankingSkeleton({ limit = 3 }: Pick<RankingProps, 'limit'>) {
    return (
        <Flex as={Section} pt="8" pb="8" direction="column" gap="4" flex>
            <Skeleton.Circle radius={56} />

            <Skeleton.Line width="40%" height="xl" />

            <Skeleton.Box width={120} height={42} />

            <Flex direction="column" gap="6">
                {Array.from({ length: limit }).map((_, idx) => (
                    <Flex
                        // biome-ignore lint/suspicious/noArrayIndexKey: loading placeholder, items have no identity
                        key={idx}
                        justify="space-between"
                        align="center"
                        gap="4"
                    >
                        <Skeleton.Line width="50%" />
                        <Skeleton.Line width="20%" />
                    </Flex>
                ))}
            </Flex>
        </Flex>
    );
}
